import {
    faCircleXmark,
    faLocationCrosshairs
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

import {
    getCurrentPosition,
    nearestLocation,
    searchLocations,
} from "../utils/locations";

// Backend hledá až od dvou znaků, jedno písmeno by nikoho nenašlo
const MIN_QUERY_LENGTH = 2;
// Hledáme ve vlastní databázi, odpověď je prakticky okamžitá
const SEARCH_DEBOUNCE_MS = 150;

/**
 * Autocomplete lokalit nad seznamem obcí v databázi.
 * value = { label, city, postcode, latitude, longitude } nebo null
 */
const LocationAutocomplete = ({
    value,
    onChange,
    placeholder = "Začněte psát obec nebo město",
    id,
    variant = "down",
    disabled = false,
    withGeolocation = true,
}) => {
    const [query, setQuery] = useState(value?.label || "");
    const [suggestions, setSuggestions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isLocating, setIsLocating] = useState(false);
    const [error, setError] = useState("");
    // Pro jaký dotaz už máme výsledky – do té doby se v dropdownu ukazuje načítání
    const [loadedTerm, setLoadedTerm] = useState("");
    // Vynutí nového hledání, když se uživatel vrátí do prázdného pole
    const [searchNonce, setSearchNonce] = useState(0);

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    // Rozpracované hledání, které se má zrušit při zavření nabídky
    const timeoutRef = useRef(null);
    const abortRef = useRef(null);
    const isOpenRef = useRef(false);
    const selectedLabel = value?.label || "";
    // Název lokality, který je už zapsaný v inputu
    const syncedLabel = useRef(selectedLabel);

    const term = query.trim();
    const isSearching =
        term.length >= MIN_QUERY_LENGTH && term !== selectedLabel;
    // Loader musí být vidět už při prvním stisku klávesy, ne až po běhu efektu
    const isPending = isSearching && loadedTerm !== term;

    // Zrušení rozpracovaného hledání (debounce i rozjetý požadavek)
    const cancelPendingSearch = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    };

    // Otevření a zavření nabídky – zavření ukončí i rozpracované hledání
    const openSuggestions = () => {
        isOpenRef.current = true;
        setIsOpen(true);
    };

    const closeSuggestions = () => {
        isOpenRef.current = false;
        setIsOpen(false);
        setActiveIndex(-1);
        cancelPendingSearch();
    };

    // Synchronizace textu s lokací přicházející zvenčí (např. načtení inzerátu
    // při editaci). Zapisuje se jen poslední hodnota, kterou jsme do inputu
    // dali my – jinak by první zmazaný znak vybrané lokality přepsal efekt a
    // smazal všechno, co uživatel právě píše.
    useEffect(() => {
        if (syncedLabel.current !== selectedLabel) {
            syncedLabel.current = selectedLabel;
            setQuery(selectedLabel);
        }
    }, [selectedLabel]);

    // Zavření našeptávání při kliknutí mimo komponentu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!wrapperRef.current?.contains(event.target)) {
                closeSuggestions();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Našeptávání s prodlevou, aby se nestřílelo po každém stisku klávesy
    useEffect(() => {
        if (!isSearching) {
            setSuggestions([]);
            return;
        }

        const controller = new AbortController();
        abortRef.current = controller;

        const timeoutId = setTimeout(async () => {
            try {
                const results = await searchLocations(term, {
                    signal: controller.signal,
                });
                // Mezitím uživatel klikl do inputu, výsledky zahodíme
                if (!isOpenRef.current) return;

                setSuggestions(results);
                setActiveIndex(-1);
                setError("");
            } catch (err) {
                if (err.name === "AbortError") return;
                setSuggestions([]);
                setError("Lokality se nepodařilo načíst.");
            } finally {
                if (isOpenRef.current) {
                    setLoadedTerm(term);
                }
            }
        }, SEARCH_DEBOUNCE_MS);

        timeoutRef.current = timeoutId;

        return () => {
            controller.abort();
            clearTimeout(timeoutId);
            abortRef.current = null;
            timeoutRef.current = null;
        };
    }, [term, isSearching, searchNonce]);

    const selectLocation = (location) => {
        cancelPendingSearch();
        onChange(location);
        syncedLabel.current = location.label;
        setQuery(location.label);
        closeSuggestions();
        setSuggestions([]);
        inputRef.current.blur()
        setError("");
    };

    const clearLocation = () => {
        cancelPendingSearch();
        onChange(null);
        syncedLabel.current = "";
        setQuery("");
        setSuggestions([]);
        closeSuggestions();
        inputRef.current.blur()
        setError("");
    };

    // Určení aktuální polohy a převod na nejbližší obec/město
    const handleGeolocation = async () => {
        setIsLocating(true);
        openSuggestions();
        setError("");

        try {
            const position = await getCurrentPosition();
            const location = await nearestLocation(
                position.latitude,
                position.longitude,
            );
            selectLocation(location);
            inputRef.current.blur()
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLocating(false);
        }
    };

    // Řádek "Použít mou aktuální polohu" se zobrazí jen do prvního napsaného písmene
    const showGeoRow = isOpen && withGeolocation && !isSearching && !isLocating;
    // Loader a prázdný výsledek se nikdy nepřekrývají – obojí je překryv nad seznamem
    const showLoader = isOpen && (isPending || isLocating);
    const showEmpty =
        isOpen && !showLoader && isSearching && suggestions.length === 0;
    // Odkud začíná index v seznamu výsledků (kvůli řádku s polohou)
    const suggestionOffset = showGeoRow ? 1 : 0;
    const rowCount = suggestions.length + suggestionOffset;
    // Podkladem překryvu musí být nějaký obsah, jinak by neměl co překrýt
    const hasRows = showGeoRow || suggestions.length > 0;

    const handleKeyDown = (event) => {
        // Pod překryvem jsou staré výsledky, nelze je vybrat
        if (showLoader && event.key !== "Escape") return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            openSuggestions();
            setActiveIndex((prev) => (prev < rowCount - 1 ? prev + 1 : 0));
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : rowCount - 1));
            return;
        }

        if (event.key === "Enter") {
            if (!isOpen || activeIndex < 0) return;

            if (showGeoRow && activeIndex === 0) {
                event.preventDefault();
                handleGeolocation();
                return;
            }

            const suggestion = suggestions[activeIndex - suggestionOffset];

            if (suggestion) {
                event.preventDefault();
                selectLocation(suggestion);
            }
            return;
        }

        if (event.key === "Escape") {
            closeSuggestions();
        }
    };

    const optionsClass = variant === "up" ? "options-up" : "options-down";
    const hasOptions =
        showGeoRow || showLoader || showEmpty || suggestions.length > 0;

    return (
        <div
            className={`location-autocomplete ${
                value ? "has-value" : ""
            }`}
            ref={wrapperRef}
        >
            <div
                className={`location-autocomplete-field ${
                    isOpen && hasOptions ? `open ${optionsClass}` : ""
                }`}
            >
                <input
                    id={id}
                    type="text"
                    ref={inputRef}
                    className="input-location"
                    placeholder={placeholder}
                    value={query}
                    disabled={disabled}
                    autoComplete="off"
                    onChange={(e) => {
                        // Po výběru zůstává input ve focusu, takže už se onFocus
                        // nespustí – psaní musí nabídku samo otevřít
                        openSuggestions();
                        setQuery(e.target.value);
                        setActiveIndex(-1);
                        // Ručně psaný text znamená, že lokalita už není platná
                        if (value) {
                            syncedLabel.current = "";
                            onChange(null);
                        }
                    }}
                    onFocus={() => {
                        openSuggestions();
                        setActiveIndex(-1);
                        // Hledání bylo zrušené zavřením – po návratu ho spustíme znovu
                        if (isPending) {
                            setSearchNonce((nonce) => nonce + 1);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                />
                {value && (
                    <button
                        type="button"
                        className="location-autocomplete-clear"
                        onClick={clearLocation}
                        aria-label="Vymazat lokalitu"
                    >
                        <FontAwesomeIcon icon={faCircleXmark} />
                    </button>
                )}
            </div>

            {error && <p className="location-autocomplete-error">{error}</p>}

            {isOpen && hasOptions && (
                <div
                    className={`location-autocomplete-options ${optionsClass} ${
                        showLoader ? "is-loading" : ""
                    }`}
                >
                    {showGeoRow && (
                        <div
                            className={`location-autocomplete-option location-autocomplete-option-geo ${
                                activeIndex === 0 ? "active" : ""
                            }`}
                            onMouseEnter={() => setActiveIndex(0)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleGeolocation}
                        >
                            <FontAwesomeIcon icon={faLocationCrosshairs} />
                            <p>Použít mou aktuální polohu</p>
                        </div>
                    )}

                    {suggestions.map((suggestion, index) => (
                        <div
                            key={`${suggestion.latitude}-${suggestion.longitude}-${index}`}
                            className={`location-autocomplete-option ${
                                index + suggestionOffset === activeIndex
                                    ? "active"
                                    : ""
                            }`}
                            onMouseEnter={() =>
                                setActiveIndex(index + suggestionOffset)
                            }
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectLocation(suggestion)}
                        >
                            <span className="label">{suggestion.label}</span>
                            {suggestion.postcode && (
                                <span className="postcode">
                                    {suggestion.postcode}
                                </span>
                            )}
                        </div>
                    ))}

                    {/* Rezerva, aby překryv měl výšku i bez řádků pod sebou */}
                    {(showLoader || showEmpty) && !hasRows && (
                        <div className="location-autocomplete-placeholder" />
                    )}

                    {(showLoader || showEmpty) && (
                        <div className="location-autocomplete-status">
                            <div className="location-autocomplete-status-child">
                                {showLoader ? (
                                    <>
                                        <div className="loader loader-smaller" />
                                    </>
                                ) : (
                                    <p className="empty">
                                        Nebyla nalezena žádná lokalita
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationAutocomplete;
